import { create } from 'zustand';
import { VideoStreamController } from '@/lib/video-stream-controller';
import type { AudioSocketService } from '@/lib/audio-socket-service';
import { trackEvent } from '@/lib/gtag';
import {
  createAuthLoginSessionId,
  getOrCreateAuthLoginSessionId,
} from '@/lib/capabilities-intro-video';
import { clearUserBrowserStorage } from '@/lib/clear-user-browser-storage';
import { AuthRateLimitError } from '@/lib/otp';

import { WorkExperience, Skill, Project, Education, Certification } from './types';

/** Backend scrape state for personal repository additional URLs */
export type AdditionalUrlScrapeStatus = 'processing' | 'failed' | 'successful';

export type AdditionalUrlLink = {
  url: string;
  title: string;
  status?: AdditionalUrlScrapeStatus;
};

export type GoogleDriveDocument = {
  id?: string;
  google_drive_id?: string;
  name: string;
  mimeType?: string;
  mime_type?: string;
  link?: string;
  status?: string;
};

/**
 * User and TalentProfile types extracted from backend login response structure.
 */

interface TalentProfile {
  portfolio_url: string | null;
  bio: string | null;
  hourly_rate: string | null;
  years_of_experience: number | null;
  annual_rate: string | null;
  primary_role: string | null;
  industry_preference: string | null;
  skills: Skill[] | null;
  soft_skills: Skill[] | null;
  company_preferences: string[] | null;
  languages: string[] | null;
  work_preferences: string[] | null;
  linkedin_profile: string | null;
  team_preferences: string[] | null;
  user_id: string;
  profile_summary: string;
  desired_role: string;
  github_profile: string | null;
  assignments: string[] | null;
  projects: Project[];
  education: Education[];
  work_experience: WorkExperience[];
  certifications: Certification[];
  avatar_details?: {
    idle?: string;
    intro?: string;
    background?: string;
    picture?: string;
    voice?: string;
  };
  cv_url?: string;
  voice_id?: string;
  intel_score?: number | string | null;
  additional_urls?: AdditionalUrlLink[];
  additional_documents?: { name: string; url: string }[];
  additional_google_drive_documents?: GoogleDriveDocument[];
  career_plan?: {
    paths?: Array<{
      paths?: Array<{
        skills?: string[];
        careerName?: string;
        summary?: string;
        coordinates?: [number, number];
        experienceGap?: number;
        experienceRequired?: number;
        softSkillsRequired?: string[];
        experienceGapToDesiredRole?: number;
      }>;
      skills?: string[];
      careerName?: string;
      summary?: string;
      coordinates?: [number, number];
      experienceGap?: number;
      experienceRequired?: number;
      softSkillsRequired?: string[];
      experienceGapToDesiredRole?: number;
    }>;
    desiredRole?: {
      skills?: string[];
      careerName?: string;
      summary?: string;
      softSkills?: string[];
      coordinates?: [number, number];
      experienceGap?: number;
      experienceRequired?: number;
    };
    postDesiredRole?: {
      skills?: string[];
      careerName?: string;
      summary?: string;
      softSkills?: string[];
      coordinates?: [number, number];
      experienceGap?: number;
      experienceRequired?: number;
    };
  };
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string | null;
  status: string;
  role: string;
  email_verified: boolean;
  phone: string | null;
  phone_verified: boolean;
  city: string | null;
  state: string | null;
  country: string | null;
  country_code: string | null;
  time_zone: string | null;
  is_active: boolean;
  is_superuser: boolean;
  created_at: string;
  updated_at: string;

  access_token: string;
  token_type: string;
  talent: TalentProfile;
  profile_picture: string;
  llm_primary?: string | null;
  llm_fallback?: string | null;
  referrer_code?: string;
  referral_count?: number;
  progress?: {
    completion_percentage: string;
    steps: Array<{ slug: string; completed: boolean }>;
    has_next_step: boolean;
    next_step: string;
    prompt_for_next_step: string;
  };
  guardrail_topics?: string | null;
  additional_google_drive_documents?: GoogleDriveDocument[];
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  videoBlob: Blob | null;
  audioCache: Map<string, Blob>;
  videoCache: Map<string, Blob>;
  onVideoEnded?: () => void;
  onVideoPlay?: () => void;
  setOnVideoEnded: (callback: (() => void) | undefined) => void;
  setOnVideoPlay: (callback: (() => void) | undefined) => void;
  /** Intro ended before VoiceChat registered `onVideoEnded`; flush once when connected. */
  pendingListeningAfterIntro: boolean;
  setPendingListeningAfterIntro: (pending: boolean) => void;
  videoStreamController: VideoStreamController | null;
  pendingVideoChunks: Uint8Array[];
  hlsUrl: string | null;
  setHlsUrl: (url: string | null) => void;
  textChatService: AudioSocketService | null;
  setTextChatService: (service: AudioSocketService | null) => void;
  fingerprint: string | null;
  setFingerprint: (fp: string) => void;
  startVideoStream: (videoEl: HTMLVideoElement) => void;
  appendVideoChunk: (chunk: Uint8Array) => void;
  destroyVideoStream: () => void;
  // Accepts the full login response now
  login: (user: User) => void;
  /** Revokes the session via POST /auth/logout, then clears local state. Keeps the token on 503/network failure. */
  logout: () => Promise<{ success: boolean; error?: string }>;
  checkAuth: () => void;
  refreshUser: () => Promise<void>;
  handleOtpVerification: (
    email: string,
    code: string
  ) => Promise<{ success: boolean; error?: string; retryAfterSeconds?: number }>;
  handleLinkedInAuth: (code: string) => Promise<{ success: boolean; error?: string }>;
  handleGitHubAuth: (code: string) => Promise<{ success: boolean; error?: string }>;
  handleAutoLogin: (encodedData: string) => Promise<{ success: boolean; error?: string }>;
  setVideoBlob: (blob: Blob | null) => void;
  updateProfilePicture: (profilePicture: string) => void;
  updateTalentData: (talentData: Partial<TalentProfile>) => void;
  updateAdditionalUrls: (links: AdditionalUrlLink[]) => void;
  updateAdditionalDocuments: (files: { name: string; url: string }[]) => void;
  updateUserData: (data: Partial<User>) => void;
  cacheAudio: (messageId: string, audioBlob: Blob) => void;
  getAudio: (messageId: string) => Blob | undefined;
  clearAudioCache: () => void;
  cacheVideo: (messageId: string, videoBlob: Blob) => void;
  getVideo: (messageId: string) => Blob | undefined;
  clearVideoCache: () => void;
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  videoBlob: null,
  audioCache: new Map(),
  videoCache: new Map(),
  onVideoEnded: undefined,
  onVideoPlay: undefined,
  setOnVideoEnded: (callback) => set({ onVideoEnded: callback }),
  setOnVideoPlay: (callback) => set({ onVideoPlay: callback }),
  pendingListeningAfterIntro: false,
  setPendingListeningAfterIntro: (pending) => set({ pendingListeningAfterIntro: pending }),
  videoStreamController: null,
  pendingVideoChunks: [],
  hlsUrl: null,
  setHlsUrl: (url) => set({ hlsUrl: url }),
  textChatService: null,
  setTextChatService: (service) => set({ textChatService: service }),
  fingerprint: null,
  setFingerprint: (fp) => set({ fingerprint: fp }),
  startVideoStream: (videoEl: HTMLVideoElement) => {
    const { videoStreamController } = get();
    if (videoStreamController) {
      console.warn('⚠️ Video stream already active');
      return;
    }
    trackEvent('video_stream_initialized', {});

    // Detect iOS for priming playback in user gesture context
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    const controller = new VideoStreamController(videoEl, undefined, isIOS);

    // Set up the stream ended callback
    controller.setOnStreamEnded(() => {
      console.log('🎬 Video stream ended, calling onVideoEnded callback');
      // Read current onVideoEnded from store (not stale closure)
      const { onVideoEnded: currentOnVideoEnded } = get();
      if (currentOnVideoEnded) {
        currentOnVideoEnded();
      } else {
        console.warn('⚠️ onVideoEnded callback not set in store');
      }
    });

    const { pendingVideoChunks } = get();
    if (pendingVideoChunks.length > 0) {
      pendingVideoChunks.forEach((chunk) => {
        controller.appendChunk(chunk);
      });
    }
    set({ videoStreamController: controller, pendingVideoChunks: [] });
  },
  appendVideoChunk: (chunk: Uint8Array) => {
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // iOS must never accept MP4 chunks
    if (isIOS) {
      return;
    }

    const { videoStreamController } = get();
    if (videoStreamController) {
      videoStreamController.appendChunk(chunk);
    } else {
      set((state) => ({ pendingVideoChunks: [...state.pendingVideoChunks, chunk] }));
    }
  },
  destroyVideoStream: () => {
    trackEvent('video_stream_destroyed', {});
    const { videoStreamController } = get();
    videoStreamController?.destroy();
    set({ videoStreamController: null, pendingVideoChunks: [] });
  },
  login: (user) => {
    // Save everything from the login response
    localStorage.setItem('user', JSON.stringify(user));
    // Reuse session when login() is used for mid-session user updates (e.g. progress).
    // Real auth handlers mint a fresh id via createAuthLoginSessionId.
    getOrCreateAuthLoginSessionId(user.id);
    set({ user, isAuthenticated: true, isLoading: false });
  },
  logout: async () => {
    const accessToken = get().user?.access_token?.trim();
    if (accessToken) {
      try {
        const { authService } = await import('@/lib/services');
        const { LOGOUT_FAILED_MESSAGE } = await import('@/lib/constants');
        const result = await authService.logout();
        if (result === 'unavailable') {
          return { success: false, error: LOGOUT_FAILED_MESSAGE };
        }
      } catch {
        const { LOGOUT_FAILED_MESSAGE } = await import('@/lib/constants');
        return { success: false, error: LOGOUT_FAILED_MESSAGE };
      }
    }

    const { useWorkflowsListStore } = await import('@/lib/workflows-list-store');
    const { useSocketBuildModeStore } = await import('@/lib/socket-build-mode-store');
    const textChatService = get().textChatService;
    if (textChatService) {
      textChatService.disconnect();
    }
    useWorkflowsListStore.getState().reset();
    useSocketBuildModeStore.getState().reset();
    document.cookie = 'token=; max-age=0; path=/; samesite=strict; secure';
    clearUserBrowserStorage();
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      textChatService: null,
    });
    window.location.href = '/login';
    return { success: true };
  },
  checkAuth: () => {
    // Skip during SSR/build time
    if (typeof window === 'undefined') {
      set({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }

    const token = getCookie('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      const user = JSON.parse(savedUser);
      // Reuse existing login session id on refresh; do not mint a new one.
      getOrCreateAuthLoginSessionId(user.id);
      set({ user, isAuthenticated: true, isLoading: false });

      // Refresh user data from API in background
      useAuthStore.getState().refreshUser();
    } else {
      document.cookie = 'token=; max-age=0; path=/; samesite=strict; secure';
      clearUserBrowserStorage();
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },
  refreshUser: async () => {
    const { user } = useAuthStore.getState();
    if (!user?.id || !user?.access_token) {
      console.log('❌ RefreshUser: Missing user ID or token, logging out');
      useAuthStore.getState().logout();
      return;
    }

    try {
      console.log('🔄 RefreshUser: Fetching user data for ID:', user.id);
      const { fetchCurrentUser } = await import('@/lib/user-api');
      const updatedUser = await fetchCurrentUser(user.id, user.access_token);

      console.log('✅ RefreshUser: Received updated user data:', updatedUser);

      // Check if API returned status: 0 (unauthorized)
      if (updatedUser?.status === 0) {
        console.log('❌ RefreshUser: API returned status 0, user unauthorized, logging out');
        useAuthStore.getState().logout();
        return;
      }

      if (updatedUser?.data) {
        // Preserve the access token from the current user
        const refreshedUser = {
          ...updatedUser.data,
          access_token: user.access_token,
          token_type: user.token_type,
        };

        // Update localStorage and state
        localStorage.setItem('user', JSON.stringify(refreshedUser));
        set({ user: refreshedUser, isLoading: false });
      }
    } catch (error) {
      console.error('❌ RefreshUser: Failed to refresh user data:', error);
      // Don't logout on refresh failure, just log the error
      set({ isLoading: false });
    }
  },
  handleOtpVerification: async (email: string, code: string) => {
    try {
      const { otpVerificationService } = await import('@/lib/services');
      const user = await otpVerificationService.verify(email, code);
      useAuthStore.getState().login(user);
      createAuthLoginSessionId(user.id);
      return { success: true };
    } catch (error) {
      if (error instanceof AuthRateLimitError) {
        return {
          success: false,
          error: error.message,
          retryAfterSeconds: error.retryAfterSeconds,
        };
      }
      const errorMessage = (error as Error).message;
      return { success: false, error: errorMessage };
    }
  },
  handleLinkedInAuth: async (code: string) => {
    try {
      const { linkedInAuthService } = await import('@/lib/services');
      const user = await linkedInAuthService.authenticate(code);
      useAuthStore.getState().login(user);
      createAuthLoginSessionId(user.id);
      return { success: true };
    } catch (error) {
      const errorMessage = (error as Error).message;
      return { success: false, error: errorMessage };
    }
  },
  handleGitHubAuth: async (code: string) => {
    try {
      const { gitHubAuthService } = await import('@/lib/services');
      const user = await gitHubAuthService.authenticate(code);
      useAuthStore.getState().login(user);
      createAuthLoginSessionId(user.id);
      return { success: true };
    } catch (error) {
      const errorMessage = (error as Error).message;
      return { success: false, error: errorMessage };
    }
  },
  handleAutoLogin: async (encodedData: string) => {
    try {
      const { autoLoginService } = await import('@/lib/services');
      const validation = await autoLoginService.decodeAndValidateData(encodedData);

      if (!validation.valid) {
        return { success: false, error: 'Invalid or expired token' };
      }

      const user = await autoLoginService.validateAndLogin(validation.token!, validation.email!);
      useAuthStore.getState().login(user);
      createAuthLoginSessionId(user.id);
      return { success: true };
    } catch (error) {
      const errorMessage = (error as Error).message;
      return { success: false, error: errorMessage };
    }
  },
  setVideoBlob: (blob) => set({ videoBlob: blob }),
  cacheAudio: (messageId: string, audioBlob: Blob) => {
    const { audioCache } = useAuthStore.getState();
    const newCache = new Map(audioCache);
    newCache.set(messageId, audioBlob);
    set({ audioCache: newCache });
  },
  getAudio: (messageId: string) => {
    const { audioCache } = useAuthStore.getState();
    return audioCache.get(messageId);
  },
  clearAudioCache: () => set({ audioCache: new Map() }),
  cacheVideo: (messageId: string, videoBlob: Blob) => {
    const { videoCache } = useAuthStore.getState();
    const newCache = new Map(videoCache);
    newCache.set(messageId, videoBlob);
    set({ videoCache: newCache });
  },
  getVideo: (messageId: string) => {
    const { videoCache } = useAuthStore.getState();
    return videoCache.get(messageId);
  },
  clearVideoCache: () => set({ videoCache: new Map() }),
  updateProfilePicture: (profilePicture) => {
    const { user } = useAuthStore.getState();
    if (user) {
      const updatedUser = { ...user, profile_picture: `${profilePicture}?t=${Date.now()}` };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      set({ user: updatedUser });
    }
  },
  updateTalentData: (talentData) => {
    const { user } = useAuthStore.getState();
    if (user) {
      const updatedUser = {
        ...user,
        talent: { ...user.talent, ...talentData },
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      set({ user: updatedUser });
    }
  },
  updateAdditionalDocuments: (files) => {
    const { user } = useAuthStore.getState();
    if (user) {
      const updatedUser = { ...user, talent: { ...user.talent, additional_documents: files } };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      set({ user: updatedUser });
    }
  },
  updateAdditionalUrls: (links) => {
    const { user } = useAuthStore.getState();
    if (user) {
      const updatedUser = { ...user, talent: { ...user.talent, additional_urls: links } };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      set({ user: updatedUser });
    }
  },
  updateUserData: (data) => {
    const { user } = useAuthStore.getState();
    if (user) {
      const updatedUser = { ...user, ...data };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      set({ user: updatedUser });
    }
  },
}));
